import React, { createContext, useState, useContext, ReactNode, useEffect } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { firestore } from "../firebase";
import { AssociatedTagsMap } from "../types";

interface AssociatedTagsContextType {
  associatedTags: AssociatedTagsMap;
  loading: boolean;
}

export const AssociatedTagsContext = createContext<AssociatedTagsContextType>({
  associatedTags: {},
  loading: false,
});

interface AssociatedTagsProviderProps {
  children: ReactNode;
}

export const AssociatedTagsProvider: React.FC<AssociatedTagsProviderProps> = ({ children }) => {
  const [associatedTags, setAssociatedTags] = useState<AssociatedTagsMap>({});
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    setLoading(true);

    // Create a reference to the associated tags collection
    const associatedTagsRef = collection(firestore, "tags");

    // Set up real-time listener
    const unsubscribe = onSnapshot(associatedTagsRef, (snapshot) => {
      const associatedTagsMap = snapshot.docs.reduce((acc, doc) => {
        const data = doc.data();
        // Only include tags that are active
        if (data.active === true) {
          acc[doc.id] = {
            tag: data.tag,
            active: data.active
          };
        }
        return acc;
      }, {} as AssociatedTagsMap);
      
      setAssociatedTags(associatedTagsMap);
      setLoading(false);
    }, (error) => {
      console.error("Error listening to associated tags updates:", error);
      setAssociatedTags({});
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  return (
    <AssociatedTagsContext.Provider value={{ associatedTags, loading }}>
      {children}
    </AssociatedTagsContext.Provider>
  );
};

export const useAssociatedTags = () => useContext(AssociatedTagsContext);
